import { useMemo } from 'react';
import { AccordionItem, Button, Checkbox, Search } from '@carbon/react';

import { FormattedCount } from 'components/formatted-count';
import { formatNumber } from 'utils/number';

import type { FacetBucket } from './types';
import {
  entityColor,
  facetOptions,
  optionsMatching,
  type FacetOption,
} from './utils';
import styles from './map-query-source.module.scss';

export interface FacetSectionProps<TId extends string | number> {
  /** "Entity" or "Location"; the title adds the ticked count. */
  title: string;
  /** Prefixes each checkbox's id. */
  idPrefix: string;
  searchLabel: string;
  searchText: string;
  onSearchTextChange: (text: string) => void;
  /** The search's buckets for this facet; undefined until the first search answers. */
  buckets: FacetBucket<TId>[] | undefined;
  /** The ticked options, in tick order, named as when ticked. */
  ticked: readonly { id: TId; name: string }[];
  onToggle: (option: FacetOption<TId>, checked: boolean) => void;
  onClear: () => void;
  clearLabel: string;
  /** Shown when the search returns no options. */
  emptyText: string;
  /** Shown instead when the search box's text leaves no options. */
  noMatchText: string;
  /** Ticked options show a swatch of their map colour, which follows tick order. */
  swatches?: boolean;
}

/**
 * One facet of the Map Query filter panel, as an accordion section: a search box,
 * a checkbox per option with its count, and a clear action while any is ticked.
 * The options are the search's buckets plus every ticked option they no longer
 * hold, at 0, narrowed by the search box's text. The title counts the ticks.
 *
 * @param {FacetSectionProps} props - Buckets, ticks and handlers
 * @return {JSX.Element} The accordion section
 */
export function FacetSection<TId extends string | number>({
  title,
  idPrefix,
  searchLabel,
  searchText,
  onSearchTextChange,
  buckets,
  ticked,
  onToggle,
  onClear,
  clearLabel,
  emptyText,
  noMatchText,
  swatches = false,
}: FacetSectionProps<TId>) {
  const options = useMemo(
    () => optionsMatching(facetOptions(buckets ?? [], ticked), searchText),
    [buckets, ticked, searchText]
  );
  const tickOrder = useMemo(
    () => new Map(ticked.map(({ id }, index) => [id, index])),
    [ticked]
  );

  return (
    <AccordionItem
      title={
        ticked.length > 0
          ? `${title} (${formatNumber({ value: ticked.length, minPrecision: 0 })})`
          : title
      }
      open
    >
      <Search
        size="sm"
        labelText={searchLabel}
        placeholder={searchLabel}
        value={searchText}
        onChange={(event) => onSearchTextChange(event.target.value)}
        className={styles.facetSearch}
      />
      <div className={styles.options}>
        {options.map((option) => {
          const index = tickOrder.get(option.id);
          return (
            <Checkbox
              key={String(option.id)}
              id={`${idPrefix}-${encodeURIComponent(String(option.id))}`}
              checked={index !== undefined}
              onChange={(_event, { checked }) => onToggle(option, checked)}
              labelText={
                <span className={styles.option}>
                  {swatches && index !== undefined && (
                    <span
                      className={styles.swatch}
                      // The swatch is the map legend: the same colour as the fill.
                      style={{ backgroundColor: entityColor(index) }}
                      aria-hidden="true"
                      data-testid="facet-swatch"
                    />
                  )}
                  {/* A name cut short shows whole on hover. */}
                  <span className={styles.optionName} title={option.name}>
                    {option.name}
                  </span>
                  <span className={styles.optionCount}>
                    <FormattedCount value={option.count} />
                  </span>
                </span>
              }
            />
          );
        })}
        {buckets !== undefined && options.length === 0 && (
          <p className={styles.caption}>
            {searchText.trim() === '' ? emptyText : noMatchText}
          </p>
        )}
      </div>
      {ticked.length > 0 && (
        <Button kind="ghost" size="sm" onClick={onClear}>
          {clearLabel}
        </Button>
      )}
    </AccordionItem>
  );
}
